// Pure decision logic for what the App analytics panel grid shows when it
// has no panels to render — deliberately kept out of the DOM-building code
// in app-analytics.ts so it's unit-testable on its own (see
// app-analytics-empty-state.test.ts).
//
// This exists because of a real incident: the console used to render one
// flat "No app analytics yet. Data appears once the iOS app reports
// events." for every reason the panel grid could come up empty. That
// sentence is false in the single most common case — DailyEventAggregation
// is written by a cron at 02:00 UTC covering *yesterday*
// (AggregationService.aggregateDailyEvents), so a recent window can have
// zero aggregated rows while ingest is perfectly healthy. The founder read
// that message as "the pipeline stopped" when in fact 199,002 raw events
// existed, the newest 43 seconds old. A failed request rendering the same
// way is the same class of bug — see anyRequestFailed below.

import type { AnalyticsFreshness } from '../api/analytics';

export type EmptyPanelState =
  | { kind: 'has-data' }
  | { kind: 'no-events-ever' }
  | {
      kind: 'not-yet-aggregated';
      rawEventCountLast24h: number;
      nextRunAt: string;
    }
  | { kind: 'load-failed' };

export interface EmptyPanelStateInput {
  /** Whether at least one panel currently has data to render. */
  hasVisiblePanels: boolean;
  /**
   * Whether any request feeding the panel grid actually failed (network
   * error, non-2xx, etc.) as opposed to succeeding with an empty result.
   */
  anyRequestFailed: boolean;
  /** Result of the freshness fetch; null if that request itself failed. */
  freshness: AnalyticsFreshness | null;
}

/**
 * Decide which empty state (if any) the panel grid should render.
 *
 * Order of precedence: real data always wins; a failed request is reported
 * as failed rather than guessed at (never silently rendered as "no data" —
 * we don't actually know that); only once both are ruled out do we ask
 * "has this deployment ever recorded an event at all"
 * (freshness.newestRawEventAt) to tell genuinely-empty apart from
 * not-yet-aggregated.
 */
export function resolveEmptyPanelState(
  input: EmptyPanelStateInput,
): EmptyPanelState {
  if (input.hasVisiblePanels) return { kind: 'has-data' };

  // A missing freshness result means we can't tell "no events ever" apart
  // from "not yet aggregated" either — fold it into load-failed rather than
  // guessing.
  if (input.anyRequestFailed || input.freshness === null) {
    return { kind: 'load-failed' };
  }

  if (input.freshness.newestRawEventAt === null) {
    return { kind: 'no-events-ever' };
  }

  return {
    kind: 'not-yet-aggregated',
    rawEventCountLast24h: input.freshness.rawEventCountLast24h,
    nextRunAt: input.freshness.nextDailyAggregationRunAt,
  };
}
